import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNowStrict } from 'date-fns';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from './ui/sheet';
import { Button } from './ui/button';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import UserAvatar from './UserAvatar';
import {
  MessageSquare,
  UserPlus,
  UserCheck,
  Loader2,
  ExternalLink,
  MapPin,
  Globe,
  Briefcase,
  Mail,
  ShieldCheck,
  UserCircle,
  Building2,
  CalendarPlus,
  Sparkles,
  X,
} from 'lucide-react';
import { useMessages } from '../context/MessagesContext';
import { useAuth } from '../context/AuthContext';
import PlannerQuickAddModal from './messaging/PlannerQuickAddModal';
import MessageAvailabilityHint from './messaging/MessageAvailabilityHint';
import {
  professionalStatusMeta,
  shouldShowProfessionalStatus,
  contactAvailabilityMeta,
  shouldShowContactAvailability,
} from '../lib/professionalStatus';

const API_URL = window.__BACKEND_URL__;

const ROLE_META = {
  talent: { label: 'Talent', icon: UserCircle, colour: 'text-purple-500' },
  casting_professional: { label: 'Casting Professional', icon: Briefcase, colour: 'text-blue-500' },
  agent: { label: 'Agent', icon: Building2, colour: 'text-amber-500' },
  production_company: { label: 'Production Company', icon: Building2, colour: 'text-emerald-500' },
  crew: { label: 'Crew', icon: UserCircle, colour: 'text-slate-500' },
  industry_service: { label: 'Industry Service', icon: Briefcase, colour: 'text-pink-500' },
};

/**
 * ConnectionProfileDrawer — slide-out right-hand drawer showing a
 * compact profile for a given user. Reuses the shadcn Sheet primitive
 * and the existing `/api/connections/card/{user_id}` endpoint (which
 * itself reuses per-role profile collections — no duplicated data).
 *
 * Props:
 *   open              — controlled open state
 *   onOpenChange      — open state setter
 *   userId            — target user id to load
 *   onConnectionChange(newState) — optional callback so parent lists can
 *                       update their counters in-place when the user
 *                       sends/cancels a connection request from here.
 *   onOpenConversation(user) — optional page-level opener used by MessagesPage
 *                       so drawer Message opens the centre thread pane instead
 *                       of the floating messenger window.
 */
export default function ConnectionProfileDrawer({ open, onOpenChange, userId, onConnectionChange, onOpenConversation, flushToTop = false }) {
  const navigate = useNavigate();
  const { openConversation } = useMessages();
  const { user, activeRole, userRoles = [] } = useAuth();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [plannerOpen, setPlannerOpen] = useState(false);

  useEffect(() => {
    if (!open || !userId) return;
    let alive = true;
    const load = async () => {
      setLoading(true);
      try {
        const { data: res } = await axios.get(`${API_URL}/api/connections/card/${userId}`, { withCredentials: true });
        if (alive) setData(res);
      } catch {
        if (alive) toast.error('Could not load profile');
        if (alive) setData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => { alive = false; };
  }, [open, userId]);

  const handleConnect = async () => {
    if (!data?.user?.id) return;
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/connections/${data.user.id}`, {}, { withCredentials: true });
      setData((d) => ({ ...d, connection_state: 'outgoing' }));
      onConnectionChange?.('outgoing');
      toast.success('Connection request sent');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not send request');
    } finally {
      setBusy(false);
    }
  };

  const handleRemove = async () => {
    if (!data?.user?.id) return;
    if (!window.confirm('Remove this connection?')) return;
    setBusy(true);
    try {
      await axios.delete(`${API_URL}/api/connections/${data.user.id}`, { withCredentials: true });
      setData((d) => ({ ...d, connection_state: 'none' }));
      onConnectionChange?.('none');
      toast.success('Connection removed');
    } catch {
      toast.error('Failed');
    } finally {
      setBusy(false);
    }
  };

  const handleMessage = async () => {
    if (!data?.user?.id) return;
    try {
      if (onOpenConversation) {
        onOpenChange(false);
        await onOpenConversation(data.user);
        return;
      }

      const { data: conv } = await axios.post(
        `${API_URL}/api/conversations`,
        { recipient_id: data.user.id },
        { withCredentials: true }
      );
      onOpenChange(false);
      openConversation(conv.id);
    } catch {
      onOpenChange(false);
      navigate(`/messages?user=${data.user.id}`);
    }
  };
  const viewFullProfile = () => {
    onOpenChange(false);
    navigate(data?.public_profile_path || `/talent/${data?.user?.id}`);
  };

  const u = data?.user;
  const p = data?.profile || {};

  const actorRoles = [
    ...(Array.isArray(userRoles) ? userRoles : []),
    ...(Array.isArray(user?.roles) ? user.roles : []),
  ].map((role) => String(role || '').trim().toLowerCase());

  const isAdminActor =
    String(activeRole || user?.active_role || user?.role || '')
      .trim()
      .toLowerCase() === 'admin' ||
    String(user?.role || '').trim().toLowerCase() === 'admin' ||
    actorRoles.includes('admin');
  const roleKey = (u?.role || '').toLowerCase();
  const roleMeta = ROLE_META[roleKey] || { label: u?.role || '', icon: UserCircle, colour: 'text-theme-muted' };
  const RoleIcon = roleMeta.icon;
  const initials = (u?.name || '?').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase();

  const locationBits = [p.city, p.region, p.country].filter(Boolean).join(', ');
  const lastSeen = u?.last_active_at ? (() => {
    try { return `${formatDistanceToNowStrict(new Date(u.last_active_at))} ago`; } catch { return ''; }
  })() : '';

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showClose={false}
        className="ccuk-connection-profile-sheet flex w-full min-h-0 flex-col !gap-0 overflow-hidden bg-theme-surface !p-0 sm:max-w-[440px]"
        data-testid="connection-profile-drawer"
        data-flush-to-top={flushToTop ? 'true' : undefined}
      >
        <button
            type="button"
            onClick={() => onOpenChange(false)}
            aria-label="Close profile"
            title="Close profile"
            data-testid="connection-profile-drawer-close"
            className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] z-40 flex h-11 w-11 items-center justify-center rounded-xl border border-white/40 bg-black/45 text-white shadow-md backdrop-blur-sm transition hover:bg-black/65 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white sm:right-4"
          >
            <X className="h-5 w-5" aria-hidden="true" />
        </button>
        {loading || !data ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-5 w-5 animate-spin text-theme-muted" />
          </div>
        ) : (
          <>
            {/* Hero */}
            <div className="relative shrink-0" data-testid="connection-profile-hero">
              {p.cover_image ? (
                <div
                  className="h-24 w-full bg-cover bg-center"
                  style={{ backgroundImage: `url(${p.cover_image})` }}
                />
              ) : (
                <div className="h-24 w-full bg-gradient-to-br from-[#002FA7] to-[#0055FF]" />
              )}

              <SheetHeader className="px-5 pt-4 pb-0 text-left sr-only">
                <SheetTitle className="sr-only">{u?.name}</SheetTitle>
                <SheetDescription className="sr-only">Compact profile for {u?.name}</SheetDescription>
              </SheetHeader>

              <div className="absolute top-24 left-5 -translate-y-1/2">
                <UserAvatar
                  user={{ ...(u || {}), ...(p || {}) }}
                  className="h-20 w-20 ring-4 ring-theme-surface"
                  fallbackClassName="text-xl bg-[#002FA7] text-white"
                  nameOverride={u?.name}
                />
                {(p.verified || p.is_verified) && (
                  <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-[#002FA7] ring-2 ring-theme-surface flex items-center justify-center" title="Verified">
                    <ShieldCheck className="h-3.5 w-3.5 text-white" />
                  </div>
                )}
              </div>
            </div>

            {/* Body */}
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-5 pt-12 pb-[max(1rem,env(safe-area-inset-bottom))]">
              <div className="mb-3" data-testid="drawer-identity">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-lg font-bold text-theme-text truncate">{u?.name}</h3>
                  {(() => {
                    // Professional status pill — only renders for the
                    // non-baseline values (on_set, auditioning, …).
                    // `available` stays silent so the drawer header
                    // doesn't shout an empty badge at the viewer.
                    if (!shouldShowProfessionalStatus(u?.professional_status)) return null;
                    const meta = professionalStatusMeta(u.professional_status);
                    if (!meta) return null;
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-theme-secondary text-theme-text font-semibold"
                        data-testid="drawer-professional-status"
                        title={`Professional status: ${meta.label}`}
                      >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                        {meta.label}
                      </span>
                    );
                  })()}
                  {(() => {
                    // Contact availability pill — same rule: `open` is
                    // the silent baseline; `limited` / `unavailable`
                    // render so viewers know to expect a slower or no
                    // reply.
                    if (!shouldShowContactAvailability(u?.contact_availability)) return null;
                    const meta = contactAvailabilityMeta(u.contact_availability);
                    if (!meta) return null;
                    return (
                      <span
                        className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-theme-secondary text-theme-text font-semibold"
                        data-testid="drawer-contact-availability"
                        title={`Contact availability: ${meta.label}`}
                      >
                        <span className={`inline-block h-1.5 w-1.5 rounded-full ${meta.dot}`} aria-hidden="true" />
                        {meta.label}
                      </span>
                    );
                  })()}
                </div>
                <div className={`flex items-center gap-1.5 text-xs ${roleMeta.colour} font-medium mt-0.5`}>
                  <RoleIcon className="h-3.5 w-3.5" />
                  {roleMeta.label}
                  {p.title && <span className="text-theme-muted font-normal"> · {p.title}</span>}
                  {p.company_name && <span className="text-theme-muted font-normal"> · {p.company_name}</span>}
                </div>
                {(locationBits || lastSeen) && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-theme-muted mt-1.5">
                    {locationBits && (
                      <span className="inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{locationBits}</span>
                    )}
                    {lastSeen && <span>Active {lastSeen}</span>}
                  </div>
                )}
              </div>

              {/* Quick actions */}
              <div className="grid grid-cols-2 gap-2 mb-4" data-testid="drawer-actions">
                {(isAdminActor || u?.messaging_enabled !== false) && data.connection_state !== 'self' && (
                  <Button
                    onClick={handleMessage}
                    className="bg-[#002FA7] hover:bg-[#0055FF] text-white gap-1.5 min-h-11 sm:min-h-9"
                    data-testid="drawer-message-btn"
                  >
                    <MessageSquare className="h-3.5 w-3.5" /> Message
                  </Button>
                )}
                {data.connection_state === 'none' && (
                  <Button
                    onClick={handleConnect}
                    disabled={busy}
                    variant="outline"
                    className="border-theme text-theme-text gap-1.5 min-h-11 sm:min-h-9"
                    data-testid="drawer-connect-btn"
                  >
                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
                    Connect
                  </Button>
                )}
                {data.connection_state === 'outgoing' && (
                  <Button variant="outline" disabled className="border-theme text-theme-muted gap-1.5 min-h-11 sm:min-h-9" data-testid="drawer-pending-btn">
                    <Loader2 className="h-3.5 w-3.5" /> Request sent
                  </Button>
                )}
                {data.connection_state === 'incoming' && (
                  <Button
                    onClick={() => { onOpenChange(false); navigate('/dashboard?tab=connections'); }}
                    variant="outline"
                    className="border-theme text-theme-text gap-1.5 min-h-11 sm:min-h-9"
                    data-testid="drawer-incoming-btn"
                  >
                    <UserPlus className="h-3.5 w-3.5" /> Review request
                  </Button>
                )}
                {data.connection_state === 'connected' && (
                  <Button
                    onClick={handleRemove}
                    disabled={busy}
                    variant="outline"
                    className="border-theme text-theme-muted hover:text-red-500 gap-1.5 min-h-11 sm:min-h-9"
                    data-testid="drawer-connected-btn"
                  >
                    <UserCheck className="h-3.5 w-3.5" /> Connected
                  </Button>
                )}
              </div>

              {/* Friendly availability hint — only renders when the
                  peer is `limited` or `unavailable`. Privacy-aware via
                  the same usePresence hook the drawer header uses. */}
              {(isAdminActor || u?.messaging_enabled !== false) && data.connection_state !== 'self' && (
                <MessageAvailabilityHint
                  recipientId={u?.id}
                  recipientName={u?.name}
                  className="mb-4"
                />
              )}

              <div className="grid grid-cols-2 gap-2 mb-4">
                <Button
                  onClick={viewFullProfile}
                  variant="outline"
                  className="border-theme text-theme-text gap-1.5 min-h-11 sm:min-h-9 text-xs"
                  data-testid="drawer-view-full-btn"
                >
                  <ExternalLink className="h-3 w-3" /> View full profile
                </Button>
                <Button
                  onClick={() => setPlannerOpen(true)}
                  variant="outline"
                  className="border-theme text-theme-text gap-1.5 min-h-11 sm:min-h-9 text-xs"
                  data-testid="drawer-planner-btn"
                >
                  <CalendarPlus className="h-3 w-3" /> Add to planner
                </Button>
              </div>

              {/* Bio */}
              {p.bio && (
                <section className="mb-4" data-testid="drawer-bio">
                  <h4 className="text-[11px] font-semibold text-theme-muted uppercase tracking-wider mb-1.5">About</h4>
                  <p className="text-xs text-theme-text leading-relaxed whitespace-pre-wrap line-clamp-6">{p.bio}</p>
                </section>
              )}

              {/* Specialties / talent types */}
              {(p.talent_types?.length > 0 || p.specialties?.length > 0) && (
                <section className="mb-4" data-testid="drawer-specialties">
                  <h4 className="text-[11px] font-semibold text-theme-muted uppercase tracking-wider mb-1.5">Specialities</h4>
                  <div className="flex flex-wrap gap-1.5">
                    {(p.talent_types || p.specialties || []).slice(0, 10).map((t) => (
                      <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-[#002FA7]/10 text-[#002FA7] font-medium">
                        {t}
                      </span>
                    ))}
                  </div>
                </section>
              )}

              {/* Years of experience */}
              {p.years_experience != null && (
                <section className="mb-4">
                  <h4 className="text-[11px] font-semibold text-theme-muted uppercase tracking-wider mb-1.5">Experience</h4>
                  <p className="text-xs text-theme-text">{p.years_experience} years</p>
                </section>
              )}

              {/* Representation / Agent details */}
              {data.representations?.length > 0 && (
                <section className="mb-4" data-testid="drawer-representations">
                  <h4 className="text-[11px] font-semibold text-theme-muted uppercase tracking-wider mb-1.5 flex items-center gap-1.5">
                    <Sparkles className="h-3 w-3 text-amber-400" /> Representation
                  </h4>
                  <div className="space-y-2">
                    {data.representations.map((r, idx) => (
                      <div key={idx} className="p-2.5 rounded-lg bg-theme-secondary border border-theme">
                        <div className="text-xs font-semibold text-theme-text">{r.agency_name || r.agent_name || 'Agency'}</div>
                        {r.agent_name && r.agency_name && (
                          <div className="text-[11px] text-theme-muted">{r.agent_name}{r.role ? ` · ${r.role}` : ''}</div>
                        )}
                        {r.email && (
                          <a href={`mailto:${r.email}`} className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-0.5">
                            <Mail className="h-3 w-3" />{r.email}
                          </a>
                        )}
                        {r.website && (
                          <a href={r.website} target="_blank" rel="noreferrer" className="text-[11px] text-[#002FA7] hover:underline inline-flex items-center gap-1 mt-0.5 ml-2">
                            <Globe className="h-3 w-3" />Website
                          </a>
                        )}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Website / contact */}
              {(p.website || u?.email) && (
                <section className="mb-2">
                  <h4 className="text-[11px] font-semibold text-theme-muted uppercase tracking-wider mb-1.5">Links</h4>
                  <div className="space-y-1">
                    {p.website && (
                      <a href={p.website} target="_blank" rel="noreferrer" className="text-xs text-[#002FA7] hover:underline inline-flex items-center gap-1.5">
                        <Globe className="h-3 w-3" />{p.website.replace(/^https?:\/\//, '')}
                      </a>
                    )}
                  </div>
                </section>
              )}
            </div>
          </>
        )}

        {plannerOpen && data?.user && (
          <PlannerQuickAddModal
            open={plannerOpen}
            onClose={() => setPlannerOpen(false)}
            context={{
              source: 'network',
              other_user_id: data.user.id,
              other_user_name: data.user.name,
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}
